import type {
  StaffImportProcessingState,
  StaffImportProgress,
  StaffImportReviewRowDetail,
  StaffImportReviewRowListQuery,
  StaffImportReviewRowPage,
} from "@lafayette/schemas";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { useImportRequest } from "../queries";
import type { RowOutcome } from "./labels";
import { isWorking, jobPhase } from "./progress";

// The reads of the review screens, through the data provider named `imports` and cached by TanStack
// Query: R5 (rows, keyset pages), R6 (one row) and R7 (progress). R7 also holds the job and its version,
// which every write needs. It is polled while the server still works on the job.

const REVIEW_ROW_PAGE_SIZE = 30;
/** How often R7 is read while a job validates or processes. A test sets a shorter time in the runtime. */
export const DEFAULT_PROGRESS_POLL_MS = 3000;

export type ReviewFilter = Readonly<{
  outcome: RowOutcome | null;
  needsDecision: boolean;
  processing: StaffImportProcessingState | null;
}>;

export const reviewKeys = {
  progress: (jobId: string) => ["imports", "progress", jobId] as const,
  rowsOf: (jobId: string) => ["imports", "review-rows", jobId] as const,
  rows: (jobId: string, filter: ReviewFilter) => ["imports", "review-rows", jobId, filter] as const,
  row: (jobId: string, rowId: string) => ["imports", "review-row", jobId, rowId] as const,
};

const toQuery = (filter: ReviewFilter, after: number): StaffImportReviewRowListQuery => ({
  limit: REVIEW_ROW_PAGE_SIZE,
  afterRowNumber: after,
  ...(filter.outcome === null ? {} : { outcome: filter.outcome }),
  ...(filter.needsDecision ? { needsDecision: true } : {}),
  ...(filter.processing === null ? {} : { processing: filter.processing }),
});

/** R7. Polls while the job validates or processes. */
export function useImportProgress(jobId: string, pollMs: number) {
  const request = useImportRequest();
  return useQuery({
    queryKey: reviewKeys.progress(jobId),
    queryFn: ({ signal }): Promise<StaffImportProgress> =>
      request<StaffImportProgress>({ command: "getProgress", jobId, signal }, undefined),
    refetchInterval: (query) => {
      const job = query.state.data?.job;
      return job !== undefined && isWorking(jobPhase(job)) ? pollMs : false;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** R5. The rows of a job after a filter, 30 at a time. Each page passes the last row number of the page before. */
export function useReviewRows(jobId: string, filter: ReviewFilter, enabled: boolean) {
  const request = useImportRequest();
  return useInfiniteQuery({
    queryKey: reviewKeys.rows(jobId, filter),
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }): Promise<StaffImportReviewRowPage> =>
      request<StaffImportReviewRowPage>(
        { command: "listReviewRows", jobId, signal },
        toQuery(filter, pageParam),
      ),
    getNextPageParam: (lastPage) => lastPage.nextAfterRowNumber,
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** R6. One row with everything the drawer shows. */
export function useReviewRow(jobId: string, rowId: string | null) {
  const request = useImportRequest();
  return useQuery({
    queryKey: reviewKeys.row(jobId, rowId ?? ""),
    queryFn: ({ signal }): Promise<StaffImportReviewRowDetail> =>
      request<StaffImportReviewRowDetail>(
        { command: "getReviewRow", jobId, signal },
        { rowId: rowId as string },
      ),
    enabled: rowId !== null,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
